import { App as NativeApp } from "@capacitor/app";
import { LegalGate } from "../legal/LegalGate";
import "../styles/MundelyUiRefinement.css";
import { lazy, Suspense, useEffect, useState } from "react";
import { Capacitor, SystemBars, SystemBarsStyle, SystemBarType } from "@capacitor/core";
import { useAuth } from "../auth/AuthProvider";
const AuthScreen = lazy(() => import("../auth/AuthScreen").then((module) => ({ default: module.AuthScreen })));
const CompleteSocialProfileScreen = lazy(() => import("../auth/CompleteSocialProfileScreen").then((module) => ({ default: module.CompleteSocialProfileScreen })));
const PendingDeletionScreen = lazy(() => import("../auth/PendingDeletionScreen").then((module) => ({ default: module.PendingDeletionScreen })));
const PublicProfilePage = lazy(() => import("../profile/PublicProfilePage").then((module) => ({ default: module.PublicProfilePage })));
import { firebaseMissingKeys } from "../firebase/client";
import { useI18n } from "../i18n/I18nProvider";
const GuidedSetup = lazy(() => import("../onboarding/GuidedSetup").then((module) => ({ default: module.GuidedSetup })));
const WelcomeCarousel = lazy(() => import("../onboarding/WelcomeCarousel").then((module) => ({ default: module.WelcomeCarousel })));
import { LocalLockGate } from "../security/LocalLockGate";
const SharedTripPage = lazy(() => import("../trips/SharedTripPage").then((module) => ({ default: module.SharedTripPage })));
import { AppShell } from "./AppShell";
import { flushUploadQueue } from "../data/uploadQueue";
import { startFainanceTripSyncRetry } from "../integrations/fainanceLinkService";

function welcomeAlreadySeen(): boolean {
  return localStorage.getItem("tripai.welcome.seen.v1") === "1";
}

export function Root() {
  return <Suspense fallback={<main className="loading-screen"><img src="/brand/mundely_backpack_icon.png" alt="Mundely" /></main>}><RootContent /></Suspense>;
}

function RootContent() {
  const auth = useAuth();
  const { t, language, setLanguage } = useI18n();
  const [welcomeSeen, setWelcomeSeen] = useState(welcomeAlreadySeen);
  const [linkedUsername, setLinkedUsername] = useState("");
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;
    const accept = (value: string) => { try { const url = new URL(value); const username = url.searchParams.get("username") || url.searchParams.get("profile") || ""; if (url.protocol === "mundely:" && url.hostname === "profile" && /^[a-z0-9._-]{3,30}$/i.test(username) && !cancelled) setLinkedUsername(username); } catch { /* Ignore unrelated auth/widget links. */ } };
    const listener = NativeApp.addListener("appUrlOpen", event => accept(event.url));
    void NativeApp.getLaunchUrl().then(result => { if (result?.url) accept(result.url); });
    return () => { cancelled = true; void listener.then(handle => handle.remove()); };
  }, []);

  useEffect(() => {
    const accountLanguage = auth.profile?.preferences?.language;
    const onboardingCompleted = Boolean(auth.profile?.onboardingCompletedAt);
    const localLanguage = typeof window !== "undefined" ? window.localStorage.getItem("tripai.language") : null;
    const canApplyAccountLanguage = onboardingCompleted || !localLanguage;
    if (accountLanguage && canApplyAccountLanguage && accountLanguage !== language) setLanguage(accountLanguage);
  }, [auth.profile?.onboardingCompletedAt, auth.profile?.preferences?.language, language, setLanguage]);

  const appTheme = auth.profile?.preferences?.theme === "dark" ? "dark" : "default";
  const themeSurface = appTheme === "dark" ? "#22272C" : "#ffffff";

  useEffect(() => {
    document.documentElement.dataset.tripTheme = appTheme;
    document.documentElement.style.colorScheme = appTheme === "dark" ? "dark" : "light";
    document.documentElement.style.setProperty("--trip-status-bar-surface", themeSurface);

    let themeColorMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
    const createdThemeColorMeta = !themeColorMeta;
    if (!themeColorMeta) {
      themeColorMeta = document.createElement("meta");
      themeColorMeta.name = "theme-color";
      document.head.appendChild(themeColorMeta);
    }
    themeColorMeta.content = themeSurface;

    return () => {
      delete document.documentElement.dataset.tripTheme;
      document.documentElement.style.removeProperty("color-scheme");
      document.documentElement.style.removeProperty("--trip-status-bar-surface");
      if (createdThemeColorMeta) themeColorMeta?.remove();
    };
  }, [appTheme, themeSurface]);

  useEffect(() => {
    let timer: number | undefined;
    const flush = () => { if (auth.user && navigator.onLine) void flushUploadQueue().catch(() => undefined); };
    const onVisibility = () => { if (document.visibilityState === "visible") flush(); };
    window.addEventListener("online", flush);
    document.addEventListener("visibilitychange", onVisibility);
    timer = window.setInterval(flush, 60000);
    flush();
    return () => {
      window.removeEventListener("online", flush);
      document.removeEventListener("visibilitychange", onVisibility);
      if (timer) window.clearInterval(timer);
    };
  }, [auth.user]);

  useEffect(() => {
    if (!auth.user) return;
    return startFainanceTripSyncRetry(auth.user.uid);
  }, [auth.user?.uid]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;
    const timers: number[] = [];
    const platform = Capacitor.getPlatform();
    const isAndroid = platform === "android";
    const dark = appTheme === "dark";

    const applyNativeStatusBar = async () => {
      if (cancelled) return;

      // Same modern SystemBars path used by fAInance. The app surface is
      // painted by CSS, not deprecated Android window-colour setters.
      await SystemBars.setStyle({ style: dark ? SystemBarsStyle.Dark : SystemBarsStyle.Light, bar: SystemBarType.StatusBar }).catch(() => undefined);
      if (cancelled) return;
      const reportedHeight = Math.max(0, parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--safe-area-inset-top")) || 0);
      const nativeInset = isAndroid
        ? Math.max(26, Math.min(reportedHeight || 28, 34))
        : Math.max(0, Math.min(reportedHeight, 52));

      document.documentElement.dataset.tripNativePlatform = platform;
      document.documentElement.dataset.tripNativeStatusMode = "overlay";
      document.documentElement.style.setProperty("--trip-status-bar-height", `${nativeInset}px`);
    };

    const reassertNativeStatusBar = () => {
      void applyNativeStatusBar();
    };

    reassertNativeStatusBar();
    // Some Android/WebView versions overwrite SYSTEM_UI_FLAG_LIGHT_STATUS_BAR shortly
    // after first paint. Re-assert after those lifecycle/paint windows as well.
    for (const delay of [80, 350, 1200]) {
      timers.push(window.setTimeout(reassertNativeStatusBar, delay));
    }

    const onVisibility = () => {
      if (document.visibilityState === "visible") reassertNativeStatusBar();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", reassertNativeStatusBar);
    window.addEventListener("pageshow", reassertNativeStatusBar);

    return () => {
      cancelled = true;
      timers.forEach((timer) => window.clearTimeout(timer));
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", reassertNativeStatusBar);
      window.removeEventListener("pageshow", reassertNativeStatusBar);
      delete document.documentElement.dataset.tripNativePlatform;
      delete document.documentElement.dataset.tripNativeStatusMode;
      document.documentElement.style.removeProperty("--trip-status-bar-height");
    };
  }, [appTheme, themeSurface]);

  if (firebaseMissingKeys.length > 0) {
    return (
      <main className="environment-shell">
        <img src="/brand/mundely_logo_horizontal.svg" className="brand-logo" alt="Mundely" />
        <h1>{t("environment.missing")}</h1>
        <p>{t("environment.missingBody")}</p>
        <code>{firebaseMissingKeys.join(", ")}</code>
      </main>
    );
  }

  const params = new URLSearchParams(window.location.search);
  const publicUsername = linkedUsername || params.get("profile");
  if (publicUsername) return <PublicProfilePage username={publicUsername} />;
  const sharedToken = params.get("share");
  if (sharedToken) return <SharedTripPage token={sharedToken} />;

  if (!welcomeSeen) {
    return <WelcomeCarousel onDone={() => {
      localStorage.setItem("tripai.welcome.seen.v1", "1");
      setWelcomeSeen(true);
    }} />;
  }

  if (auth.loading) return <main className="loading-screen"><img src="/brand/mundely_backpack_icon.png" alt="Mundely" /></main>;
  if (!auth.user) return <AuthScreen />;
  if (auth.profile?.accountStatus === "pendingDeletion") return <PendingDeletionScreen />;
  if (!auth.profile) {
    const usesPassword = auth.user.providerData.some((item) => item.providerId === "password");
    if (usesPassword) {
      return <main className="loading-screen"><img src="/brand/mundely_backpack_icon.png" alt="Mundely" /></main>;
    }
    return <CompleteSocialProfileScreen />;
  }
  if (!auth.profile.username) return <CompleteSocialProfileScreen />;
  if (!auth.profile.onboardingCompletedAt) return <LegalGate><GuidedSetup /></LegalGate>;
  return <LocalLockGate><LegalGate><AppShell /></LegalGate></LocalLockGate>;
}
